import { Component, OnInit, Input, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
// Animations
import { slideInDownAnimation } from '../../app/app.animation';
// Common
import { AngularMaterialService, ErrorParseService, IExternalData, ISetPassword } from '../../commons';
// State
import { Store } from '@ngrx/store';
import * as fromAuthentication from '../login/state/login.selectors';
import * as fromProfile from './state/profile.selectors';
import * as ProfileAction from './state/profile.actions';
import * as lgeApp from '../../app/state/app.actions';
import { Observable, skipWhile, Subscription } from 'rxjs';

@Component({
  selector: 'profile-screen',
  templateUrl: 'profile.component.html',
  styleUrls: ['profile.scss'],
  animations: [slideInDownAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileScreenComponent implements OnInit, OnDestroy {
  @Input() pending$: Observable<boolean>;
  pendingSubscription: Subscription;
  pending: boolean;
  @Input() error$: Observable<string | null>;
  errorSubscription: Subscription;
  error: string | null;
  @Input() successMessage$: Observable<string | null>;
  successMessageSubscription: Subscription;
  successMsg: string | null;
  @Input() externalProfileData$: Observable<IExternalData | null>;
  externalProfileDataSubscription: Subscription;
  externalProfileData: IExternalData | null;

  extOrgInfoFormGroup: FormGroup;
  extOrgContactInfoFormGroup: FormGroup;
  extUserFormGroup: FormGroup;
  setPasswordFormGroup: FormGroup;

  constructor(
    private store: Store<fromProfile.State>,
    private authStore: Store<fromAuthentication.State>,
    private ref: ChangeDetectorRef,
    private fb: FormBuilder,
    private ams: AngularMaterialService,
    private errorParse: ErrorParseService
  ) {
    this.selectState();
    this.createForms();
  }


  ngOnInit() {
    this.startSubscriptions();
  }

  // Select state
  selectState(): void {
    this.pending$ = this.store.select(fromProfile.getProfileScreenPending);
    this.externalProfileData$ = this.authStore.select(fromAuthentication.getExternalProfile).pipe(skipWhile(profile => profile === undefined || profile === null));
    this.error$ = this.store.select(fromProfile.getProfileScreenError).pipe(skipWhile(error => error === undefined || error === null || error === ''));
    this.successMessage$ = this.store.select(fromProfile.getSuccessMessage).pipe(skipWhile(msg => msg === undefined || msg === null));
  }

  displayLoader(pending: boolean): void {
    if (pending) {
      setTimeout(() => {
        this.ams.showLoader('Loading...');
        this.ref.markForCheck();
      })
    } else {
      this.ams.closeDialog().then(() => {
        if (this.error !== null && this.error !== undefined && this.error !== '') {
          this.displayError(this.error);
        } else if (this.successMsg !== null && this.successMsg !== undefined) {
          this.displaySuccess(this.successMsg);
        }
      });
    }
  }

  displayError(error: any): void {
    let errorMessage;
    if (this.errorParse.foundKeyWord(error, "CORPCOMMONWS")) {
      errorMessage = this.errorParse.translateCCError(error);
    } else {
      errorMessage = error instanceof Object ? error.message : error;
    }
    this.ams.showAlert("Loading Error", errorMessage);
    this.ams.alert.afterClosed().subscribe(result => {
      this.store.dispatch(new ProfileAction.ProfileScreenErrorReset());
    });
  }

  displaySuccess(msg: string): void {
    if (msg.includes('GuideStar API'))
      this.ams.showAlert("Unable to verify", msg);
    else
      this.ams.showAlert("Success", msg);
    this.ams.alert.afterClosed().subscribe(result => {
      this.store.dispatch(new ProfileAction.ProfileScreenErrorReset());
    });
  }

  getTaxExemptStatus(value) {
    let temp = value.replace(/ *\([^)]*\) */g, "");
    if (isNaN(temp)) {
      return temp;
    } else {
      return `501(c)(${+temp})`;
    }
  }

  verifyEIN() {
    let externalData: IExternalData = {
      orgInfoExt: this.extOrgInfoFormGroup.value,
      orgContactInfoExt: this.extOrgContactInfoFormGroup.value,
      externalUser: this.extUserFormGroup.value
    };
    this.store.dispatch(new ProfileAction.VerifyEIN(externalData));
  }

  // Create reactive forms
  createForms(): void {
    //Org Info
    this.extOrgInfoFormGroup = this.fb.group({
      orgInfoExtId: [null],
      name: ['', [Validators.required, Validators.maxLength(50)]],
      EIN: ['', [Validators.required, Validators.maxLength(10), Validators.pattern(new RegExp(/^\d{2}-\d{7}/))]],
      address1: ['', [Validators.required, Validators.maxLength(35)]],
      address2: ['', [Validators.maxLength(35)]],
      city: ['', [Validators.required, Validators.maxLength(50)]],
      state: ['', Validators.required],
      zipCode: ['', [Validators.required, Validators.pattern(new RegExp(/^\d{5}$|^\d{9}$/))]],
      webAddress: [''],
      subsection_501: ['', Validators.required]
    });

    //Contact Info
    this.extOrgContactInfoFormGroup = this.fb.group({
      orgContactInfoExtId: [null],
      title: [''],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phone: ['', [Validators.required, Validators.pattern(new RegExp(/^\d{3}-\d{3}-\d{4}/))]],
      email: ['', [Validators.required, Validators.maxLength(254), Validators.pattern(new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/))]],
      secondaryTitle: [''],
      secondaryFirstName: [''],
      secondaryLastName: [''],
      secondaryPhone: ['', [Validators.pattern(new RegExp(/^\d{3}-\d{3}-\d{4}/))]],
      secondaryEmail: ['', [Validators.maxLength(254), Validators.pattern(new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/))]]
    });

    //User Info
    this.extUserFormGroup = this.fb.group({
      externalUserId: [null],
      orgInfoExtId: [''],
      orgContactInfoExtId: [''],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.maxLength(254), Validators.pattern(new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/))]],
      accessStatus: [''],
      isVerified: [false]
    });

    //Password Info
    this.setPasswordFormGroup = this.fb.group({
      email: [''],
      oldPassword: ['', [Validators.required, Validators.minLength(3)]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(8)]],
      sid: ['']
    }, {
      validator: ConfirmedValidator('password', 'confirmPassword')
    });

    function ConfirmedValidator(controlName: string, matchingControlName: string) {
      return (formGroup: FormGroup) => {
        const control = formGroup.controls[controlName];
        const matchingControl = formGroup.controls[matchingControlName];
        if (matchingControl.errors && !matchingControl.errors['confirmedValidator']) {
          return;
        }
        if (control.value !== matchingControl.value) {
          matchingControl.setErrors({ confirmedValidator: true });
        } else {
          matchingControl.setErrors(null);
        }
      }
    }

  }

  // Opens sidenav if on mobile
  public openSidenav(): void {
    this.store.dispatch(new lgeApp.OpenSidenav());
  }

  submitUpdates() {
    let externalData: IExternalData = {
      orgInfoExt: this.extOrgInfoFormGroup.value,
      orgContactInfoExt: this.extOrgContactInfoFormGroup.value,
      externalUser: this.extUserFormGroup.value
    };
    this.store.dispatch(new ProfileAction.SubmitProfileDetail(externalData));
    // this.dialogRef.close(externalData);
  }

  setPassword() {
    let loginInfo: ISetPassword = this.setPasswordFormGroup.value;
    this.store.dispatch(new ProfileAction.SetPassword(loginInfo));
  }

  // Subscribe to state data and mark it for changes
  startSubscriptions(): void {
    this.pendingSubscription = this.pending$
      .subscribe(data => {
        this.pending = data;
        this.displayLoader(this.pending);
        this.ref.markForCheck();
      });
    this.errorSubscription = this.error$
      .subscribe(data => {
        this.error = data;
        this.ref.markForCheck();
      });
    this.successMessageSubscription = this.successMessage$
      .subscribe(data => {
        this.successMsg = data;
        this.ref.markForCheck();
      });
    this.externalProfileDataSubscription = this.externalProfileData$
      .subscribe(data => {
        if (data !== null) {
          this.externalProfileData = JSON.parse(JSON.stringify(data));
          this.extUserFormGroup.setValue(this.externalProfileData.externalUser)
          this.extOrgContactInfoFormGroup.setValue(this.externalProfileData.orgContactInfoExt)
          this.extOrgInfoFormGroup.setValue(this.externalProfileData.orgInfoExt)
          this.setPasswordFormGroup.get('email').setValue(this.externalProfileData.externalUser.email)
          this.ref.markForCheck();
        }
      });
  }

  // Unsubscribe from state data changes
  endSubscriptions(): void {
    this.pendingSubscription.unsubscribe();
    this.errorSubscription.unsubscribe();
    this.externalProfileDataSubscription.unsubscribe();
    this.store.dispatch(new ProfileAction.LoadInitState());
  }

  ngOnDestroy() {
    this.endSubscriptions();
  }

}